import { Loader2 } from "lucide-react";
import useSWRImmutable from "swr/immutable";
import { useProjectAppsFeature } from "~/hooks/useProjectAppsFeature";
import { cn } from "~/lib/utils";
import {
  SessionContextPanelType,
  useOptionalSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { type ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";
import { filenameFromPreview, hasFileSuffix, urlFromPreview } from "./inlineViewerPreview";

const HTML_SUFFIXES = [".html", ".htm"];

// An app template declares its manifest in this block; the project apps server checks it.
const MANIFEST_PATTERN =
  /<script\b[^>]*type\s*=\s*["']application\/vnd\.sc-app\+json["'][^>]*>[\s\S]*?<\/script>/i;

export function hasAppManifest(html: string): boolean {
  return MANIFEST_PATTERN.test(html);
}
const MAX_HTML_FILE_BYTES = 20 * 1024 * 1024;

export function isHtmlArtifact(preview: ArtifactPreview): boolean {
  return hasFileSuffix(preview, HTML_SUFFIXES);
}

interface HtmlViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

async function fetchHtml(url: string): Promise<string> {
  // A presigned S3 URL, which takes no app auth, so this bypasses apiClient.
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Server returned ${response.status}`);
  }
  const contentLength = response.headers.get("Content-Length");
  if (contentLength && Number(contentLength) > MAX_HTML_FILE_BYTES) {
    throw new Error("File is too large to render inline. Please download it instead.");
  }
  return response.text();
}

/** Fetch an HTML artifact's markup, cached per artifact version. */
export function useArtifactHtml(preview: ArtifactPreview) {
  const url = urlFromPreview(preview);
  // Keyed on the artifact version, not its URL: every preview fetch re-signs the
  // URL, and reloading on that would reset the app running in the frame.
  const {
    data: html,
    error: loadError,
    isLoading,
  } = useSWRImmutable(url ? ["artifact-html", preview.artifact_id, preview.version] : null, () =>
    fetchHtml(url!)
  );
  const error = !url
    ? "No content URL available for this artifact."
    : loadError
      ? loadError instanceof Error
        ? loadError.message
        : "Unknown error"
      : null;
  return { html, error, isLoading };
}

// Renders artifact HTML inside a sandboxed iframe via `srcDoc`. The sandbox
// grants `allow-scripts` (so interactive output like Plotly/Bokeh runs) but
// deliberately withholds `allow-same-origin` — the document lands in an opaque
// origin with no access to the app's cookies, storage, or DOM. This is why we
// can render agent-produced HTML without sanitizing it inline.
export const HtmlViewer = ({ preview, className }: HtmlViewerProps) => {
  const { html, error, isLoading } = useArtifactHtml(preview);
  const isApp = html !== undefined && hasAppManifest(html);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "h-[450px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading HTML...</p>
          </div>
        </div>
      )}
      {isApp && (
        <div className="flex h-full items-center justify-center rounded-md border border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          This is an app. Open it as an app to load its data.
        </div>
      )}
      {html !== undefined && !isApp && (
        <iframe
          title={filenameFromPreview(preview) || "HTML artifact"}
          srcDoc={html}
          sandbox="allow-scripts"
          className={cn(
            "h-full w-full rounded-md border border-border bg-white",
            isLoading && "invisible"
          )}
        />
      )}
    </div>
  );
};

export const HtmlArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  // Absent outside a project session (shared/admin views): no panel to open into.
  const sessionPanel = useOptionalSessionContextPanel();
  const { enabled: isProjectAppsEnabled } = useProjectAppsFeature();
  const handleOpenAsApp =
    sessionPanel && isProjectAppsEnabled
      ? () => {
          sessionPanel.openPanel(SessionContextPanelType.ARTIFACT_APP, preview.artifact_id);
          sessionPanel.setAppExpanded(true);
        }
      : undefined;

  return (
    <ArtifactShell
      preview={preview}
      expandedContent={<HtmlViewer preview={preview} className="h-full" />}
      onOpenAsApp={handleOpenAsApp}
    >
      <FileArtifactBody preview={preview} />
      <div className="mt-3">
        <HtmlViewer preview={preview} />
      </div>
    </ArtifactShell>
  );
};
